'use client'

import { useMemo, useState } from 'react'
import { useCallbackRef } from './use-callback-ref'

/**
 * Given a prop value and state value, the useControllableProp hook is used to determine whether a component is controlled or uncontrolled, and also returns the computed value.
 */
export function useControllableProp<T>(prop: T | undefined, state: T) {
  return useMemo<[boolean, T]>(() => {
    const controlled = typeof prop !== 'undefined'
    const value = controlled ? prop : state

    return [controlled, value]
  }, [prop, state])
}

interface UseControllableStateBaseProps<T> {
  value?: T | undefined
  defaultValue?: T | (() => T) | undefined
  shouldUpdate?: ((prev: T, next: T) => boolean) | undefined
}

/** When `onChange` has a required second (context) parameter, use this; the setter will require context. */
export interface UseControllableStatePropsWithContext<
  T,
  C,
> extends UseControllableStateBaseProps<T> {
  onChange?: (value: T, context: C) => void
}

/** When `onChange` has no second parameter or an optional one, use this; the setter's context is optional. */
export interface UseControllableStateProps<T> extends UseControllableStateBaseProps<T> {
  onChange?: (value: T) => void
}

export type ControllableStateSetter<T, C = unknown> = (
  next: React.SetStateAction<T>,
  context?: C
) => void

export type ControllableStateSetterWithContext<T, C> = (
  next: React.SetStateAction<T>,
  context: C
) => void

/**
 * The `useControllableState` hook returns the state and function that updates the state, just like React.useState does.
 */
export function useControllableState<T>(
  props: UseControllableStateProps<T>
): [T, ControllableStateSetter<T>] {
  const {
    value: valueProp,
    defaultValue,
    onChange,
    shouldUpdate = (prev: T, next: T) => prev !== next,
  } = props

  const onChangeProp = useCallbackRef(onChange as (value: T) => void)
  const shouldUpdateProp = useCallbackRef(shouldUpdate)

  const [uncontrolledState, setUncontrolledState] = useState(defaultValue as T)
  const controlled = valueProp !== undefined
  const value = controlled ? valueProp : uncontrolledState

  const setValue = useCallbackRef(
    (next: React.SetStateAction<T>) => {
      const setter = next as (prevState?: T) => T
      const nextValue = typeof next === 'function' ? setter(value) : next

      if (!shouldUpdateProp(value, nextValue)) {
        return
      }

      if (!controlled) {
        setUncontrolledState(nextValue)
      }

      // Call with optional context; when onChange requires context, the setter type requires it at the call site
      onChangeProp(nextValue)
    },
    [controlled, onChangeProp, value, shouldUpdateProp]
  )

  return [value, setValue]
}

/**
 * The `useControllableState` hook returns the state and function that updates the state, just like React.useState does.
 * When `onChange` has a **required** second (context) parameter, the setter **requires** context when called.
 * When `onChange` has no second parameter or an optional one, the setter's context argument is optional.
 */
export function useControllableStateWithContext<T, C = unknown>(
  props: UseControllableStatePropsWithContext<T, C>
): [T, ControllableStateSetterWithContext<T, C>] {
  const {
    value: valueProp,
    defaultValue,
    onChange,
    shouldUpdate = (prev: T, next: T) => prev !== next,
  } = props

  const onChangeProp = useCallbackRef(onChange)
  const shouldUpdateProp = useCallbackRef(shouldUpdate)

  const [uncontrolledState, setUncontrolledState] = useState(defaultValue as T)
  const controlled = valueProp !== undefined
  const value = controlled ? valueProp : uncontrolledState

  const setValue = useCallbackRef(
    (next: React.SetStateAction<T>, context?: C) => {
      const setter = next as (prevState?: T) => T
      const nextValue = typeof next === 'function' ? setter(value) : next

      if (!shouldUpdateProp(value, nextValue)) {
        return
      }

      if (!controlled) {
        setUncontrolledState(nextValue)
      }

      // Call with optional context; when onChange requires context, the setter type requires it at the call site
      ;(onChangeProp as (value: T, context?: C) => void)(nextValue, context)
    },
    [controlled, onChangeProp, value, shouldUpdateProp]
  )

  return [value, setValue]
}
